import {
  UseMutationOptions,
  UseQueryOptions,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { forceHideSidebar } from '@components/sidebar';
import { showSuccess } from '@components/toaster';
import { useThrottledMutation as useMutation } from '@hooks/use-throttled-mutation';
import { useScopedTranslate } from '@locale/translate';

import { useInvalidateJourneyIntegrations } from 'api/integrations/hooks';
import { useInvalidateJourneyActionForms } from 'api/journey-action-forms/hooks';
import { useInvalidateActionPreviews, useInvalidateJourneys } from 'api/journeys/hooks';
import { handleError, handlePossibleNonFieldError } from 'api/query-client';
import {
  ActionRelationship,
  ActionRelationshipItem,
  BASE_ACTION_TYPE,
  CreateJourneyActionData,
  JourneyAction,
  JourneyActionDetailed,
  JourneyType,
} from 'api/types';
import { isInTemplateRoute } from 'ui/router';

import {
  JourneyActionMoves,
  convertJourneyActionToAIAgentTemplate,
  createJourneyAction,
  createJourneyActionRelationship,
  duplicateJourneyAction,
  listJourneyActionRelationships,
  listTemplateJourneyActionRelationships,
  loadJourneyAction,
  loadJourneyActionAvailableMoves,
  loadJourneyActionIsInsideLoop,
  loadJourneyActions,
  loadJourneyTemplateActions,
  loadTemplateJourneyAction,
  moveJourneyAction,
  removeJourneyAction,
  removeJourneyActionRelationship,
  updateJourneyAction,
  updateJourneyActionDescription,
} from './requests';

const getJourneyActionsQueryKey = (shortId?: JourneyType['shortId']) =>
  ['journey', shortId, 'action'].filter(Boolean);

export const useJourneyActions = (
  journeyId: string,
  options?: { onSuccess?: (data: JourneyAction[]) => void; enabled?: boolean }
) => {
  const loadFunction = isInTemplateRoute() ? loadJourneyTemplateActions : loadJourneyActions;
  return useQuery({
    queryKey: getJourneyActionsQueryKey(journeyId),
    queryFn: () => loadFunction(journeyId),
    enabled: Boolean(journeyId),
    ...options,
  });
};

export const getJourneyActionQueryKey = (shortId?: JourneyAction['shortId']) =>
  ['journey', 'action', shortId].filter(Boolean);

export const useJourneyAction = <ActionType extends BASE_ACTION_TYPE>(
  shortId: string,
  options?: { onSuccess?: (data: JourneyActionDetailed<ActionType>) => void; enabled?: boolean }
) => {
  const loadFn = isInTemplateRoute() ? loadTemplateJourneyAction : loadJourneyAction;
  return useQuery({
    queryKey: getJourneyActionQueryKey(shortId),
    queryFn: () => loadFn<ActionType>(shortId),
    ...options,
  });
};

export const useJourneyActionIsInsideLoop = (shortId: string, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: [...getJourneyActionQueryKey(shortId), 'is-inside-loop'],
    queryFn: () => loadJourneyActionIsInsideLoop(shortId),
    ...options,
  });
};

export const useInvalidateJourneyActions = () => {
  const queryClient = useQueryClient();

  const invalidateJourneyAction = (shortId?: JourneyAction['shortId']) => {
    return queryClient.invalidateQueries(getJourneyActionQueryKey(shortId));
  };
  const invalidateJourneyActions = (shortId?: JourneyType['shortId']) => {
    return queryClient.invalidateQueries(getJourneyActionsQueryKey(shortId));
  };
  const removeJourneyActionQuery = (shortId?: JourneyAction['shortId']) => {
    return queryClient.removeQueries(getJourneyActionQueryKey(shortId));
  };
  // Every action's list at once - they are keyed by action id, which a journey id misses
  const invalidateJourneyActionRelationships = () => {
    return queryClient.invalidateQueries({
      predicate: ({ queryKey }) => queryKey.join('/').endsWith('actions/relationships'),
    });
  };

  const invalidateJourneyActionDependants = () => {
    return queryClient.invalidateQueries({
      predicate: ({ queryKey }) => queryKey.join('/').endsWith('actions/dependants'),
    });
  };

  return {
    invalidateJourneyAction,
    invalidateJourneyActions,
    invalidateJourneyActionRelationships,
    invalidateJourneyActionDependants,
    removeJourneyActionQuery,
  };
};

export const useCreateJourneyAction = (
  options?: UseMutationOptions<{ shortId: string }, any, CreateJourneyActionData>
) => {
  const { invalidateJourney } = useInvalidateJourneys();
  const { invalidateJourneyIntegrationEndpoints, invalidateJourneyIntegrationWebhooks } =
    useInvalidateJourneyIntegrations();
  const { invalidateJourneyActionForms, invalidateJourneyActionFormsList } =
    useInvalidateJourneyActionForms();
  const { t } = useScopedTranslate(locale);
  return useMutation({
    ...options,
    mutationFn: (data) => createJourneyAction(data),
    onSuccess: (_, vars) => {
      invalidateJourney(vars.journey);
      if (vars.action === 'integration' || vars.action === 'webhook') {
        invalidateJourneyIntegrationEndpoints(vars.journey);
        invalidateJourneyIntegrationWebhooks(vars.journey);
      }
      if (vars.action === 'combo_send_form') {
        invalidateJourneyActionForms();
        invalidateJourneyActionFormsList();
      }
    },
    onError: (data) => {
      handlePossibleNonFieldError(data, t('journeyActionNotSaved'));
    },
  });
};

export const useUpdateJourneyAction = (
  shortId: string,
  options?: UseMutationOptions<JourneyActionDetailed, any, object>
) => {
  const { invalidateJourney } = useInvalidateJourneys();
  const { invalidateJourneyAction } = useInvalidateJourneyActions();
  const { invalidateJourneyIntegrationEndpoints } = useInvalidateJourneyIntegrations();
  const invalidateActionPreviews = useInvalidateActionPreviews();
  const { t } = useScopedTranslate(locale);
  return useMutation({
    ...options,
    mutationFn: (data) => updateJourneyAction(shortId, data),
    onSuccess: async (...data) => {
      !options?.onSuccess && forceHideSidebar();
      options?.onSuccess?.(...data);
      await Promise.all([
        invalidateJourney(data[0].journey),
        invalidateJourneyAction(shortId),
        invalidateJourneyIntegrationEndpoints(data[0].journey),
        invalidateActionPreviews('journey', shortId),
      ]);
    },
    onError: (...data) => {
      if (options?.onError) {
        options?.onError?.(...data);
      } else {
        handlePossibleNonFieldError(data, t('journeyActionNotSaved'));
      }
    },
  });
};

export const useUpdateJourneyActionDescription = (
  shortId: string,
  journeyShortId: string,
  options?: UseMutationOptions<void, any, string>
) => {
  const { invalidateJourney } = useInvalidateJourneys();

  return useMutation({
    ...options,
    mutationFn: (data) => updateJourneyActionDescription(shortId, data),
    onSuccess: (...vars) => {
      invalidateJourney(journeyShortId);
      options?.onSuccess?.(...vars);
    },
  });
};

export const useRemoveJourneyAction = (options?: UseMutationOptions<unknown, any, string>) => {
  const { invalidateJourney } = useInvalidateJourneys();
  const { invalidateJourneyActionFormsList } = useInvalidateJourneyActionForms();
  const client = useQueryClient();
  return useMutation({
    ...options,
    mutationFn: (shortId) => removeJourneyAction(shortId),
    onSuccess: async (data, variables, context) => {
      return await Promise.all([
        client.removeQueries(getJourneyActionQueryKey(variables)),
        invalidateJourney(),
        invalidateJourneyActionFormsList(),
        options?.onSuccess?.(data, variables, context),
      ]);
    },
  });
};

const getJourneyActionRelationshipsQueryKey = (shortId: JourneyAction['shortId']) => [
  'journey',
  shortId,
  'actions',
  'relationships',
];

const useSyncJourneyAfterRelationshipChange = () => {
  const { invalidateJourneyDetail, invalidateJourneyIssues, invalidateUnpublishedChanges } =
    useInvalidateJourneys();
  const { invalidateJourneyAction, invalidateJourneyActions, invalidateJourneyActionDependants } =
    useInvalidateJourneyActions();

  return (journeyId: JourneyType['shortId']) => {
    invalidateJourneyDetail(journeyId);
    invalidateJourneyIssues(journeyId);
    invalidateUnpublishedChanges(journeyId);
    invalidateJourneyActions(journeyId);
    invalidateJourneyAction();
    invalidateJourneyActionDependants();
  };
};

export const useJourneyActionRelationships = (shortId: string) => {
  const listFn = isInTemplateRoute()
    ? listTemplateJourneyActionRelationships
    : listJourneyActionRelationships;
  return useQuery({
    queryKey: getJourneyActionRelationshipsQueryKey(shortId),
    queryFn: () => listFn(shortId),
  });
};

export const useCreateJourneyActionRelationship = (
  options?: UseMutationOptions<
    ActionRelationshipItem,
    any,
    { parentShortId: string; childShortId: string; journeyId: string }
  >
) => {
  const queryClient = useQueryClient();
  const syncJourney = useSyncJourneyAfterRelationshipChange();
  const { t } = useScopedTranslate(locale);
  return useMutation({
    ...options,
    mutationFn: (data) =>
      createJourneyActionRelationship({ parent: data.parentShortId, child: data.childShortId }),
    onSuccess: (relationship, vars, context) => {
      queryClient.setQueryData<ActionRelationship>(
        getJourneyActionRelationshipsQueryKey(vars.childShortId),
        (current) =>
          current ? { ...current, actions: [...current.actions, relationship] } : current
      );
      syncJourney(vars.journeyId);
      options?.onSuccess?.(relationship, vars, context);
    },
    onError: (data) => {
      handlePossibleNonFieldError(data, t('journeyActionNotSaved'));
    },
  });
};

export const useRemoveJourneyActionRelationship = (
  options?: UseMutationOptions<
    unknown,
    any,
    { relationshipShortId: string; childShortId: string; journeyId: string }
  >
) => {
  const queryClient = useQueryClient();
  const syncJourney = useSyncJourneyAfterRelationshipChange();
  return useMutation({
    ...options,
    mutationFn: (vars) => removeJourneyActionRelationship(vars.relationshipShortId),
    onSuccess: (data, vars, context) => {
      queryClient.setQueryData<ActionRelationship>(
        getJourneyActionRelationshipsQueryKey(vars.childShortId),
        (current) =>
          current
            ? {
                ...current,
                actions: current.actions.filter(
                  (relationship) => relationship.shortId !== vars.relationshipShortId
                ),
              }
            : current
      );
      syncJourney(vars.journeyId);
      options?.onSuccess?.(data, vars, context);
    },
  });
};

export const useJourneyActionAvailableMoves = (
  shortId: string,
  options?: UseQueryOptions<unknown, any, JourneyActionMoves>
) => {
  return useQuery({
    queryKey: ['journey', shortId, 'actions', 'moves'],
    queryFn: () => loadJourneyActionAvailableMoves(shortId),
    ...options,
  });
};

export const useMoveJourneyAction = () => {
  const { invalidateJourney } = useInvalidateJourneys();
  const { invalidateJourneyActionRelationships } = useInvalidateJourneyActions();
  const { t } = useScopedTranslate(locale);
  return useMutation({
    mutationFn: moveJourneyAction,
    onSuccess: (_, requestData) => {
      return Promise.all([
        invalidateJourney(requestData.journeyId),
        invalidateJourneyActionRelationships(),
      ]);
    },
    onError: () => {
      handleError(t('journeyActionNotMoved'));
    },
  });
};

export const useDuplicateJourneyAction = () => {
  const { invalidateJourney } = useInvalidateJourneys();
  const { invalidateJourneyActionForms, invalidateJourneyActionFormsList } =
    useInvalidateJourneyActionForms();
  const { t } = useScopedTranslate(locale);
  return useMutation({
    mutationFn: (shortId: string) => duplicateJourneyAction(shortId),
    onSuccess: () => {
      invalidateJourney();
      invalidateJourneyActionForms();
      invalidateJourneyActionFormsList();
    },
    onError: () => {
      handleError(t('journeyActionNotDuplicated'));
    },
  });
};

export const useConvertJourneyActionToAIAgentTemplate = () => {
  const queryClient = useQueryClient();
  const { t } = useScopedTranslate(locale);

  return useMutation({
    mutationFn: (shortId: string) => convertJourneyActionToAIAgentTemplate(shortId),
    onSuccess: () => {
      queryClient.invalidateQueries(['ai-agents']);
      showSuccess(t('aiAgentTemplateCreated'));
    },
    onError: () => {
      handleError(t('aiAgentTemplateNotCreated'));
    },
  });
};

const locale = {
  en: {
    journeyActionNotSaved: 'Journey action not saved',
    journeyActionNotDuplicated: 'Action not duplicated',
    journeyActionNotMoved: 'Action not moved',
    aiAgentTemplateCreated: 'AI Agent template created',
    aiAgentTemplateNotCreated: 'AI Agent template not created',
  },
};
